import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, Star } from "lucide-react";
import { formatDate } from "@/lib/utils";

export const ReviewsPanel = ({ reviews, loading, language, onDeleteReview }) => {
  const [selectedReview, setSelectedReview] = useState(null);
  const [reviewToDelete, setReviewToDelete] = useState(null);

  if (loading) {
    return <Card className="bg-card border-border p-8 text-center text-muted-foreground" data-testid="admin-reviews-loading">{language === "es" ? "Cargando reviews..." : "Loading reviews..."}</Card>;
  }
  if (!reviews.length) {
    return <Card className="bg-card border-border p-8 text-center text-muted-foreground" data-testid="admin-reviews-empty-state">{language === "es" ? "Todavía no hay reviews." : "No reviews yet."}</Card>;
  }

  return (
    <>
      <Card className="bg-card border-border overflow-hidden" data-testid="admin-reviews-panel">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{language === "es" ? "Fecha" : "Date"}</TableHead>
              <TableHead>{language === "es" ? "Usuario" : "User"}</TableHead>
              <TableHead>{language === "es" ? "Rating" : "Rating"}</TableHead>
              <TableHead>{language === "es" ? "Mensaje" : "Message"}</TableHead>
              <TableHead>{language === "es" ? "Video" : "Video"}</TableHead>
              <TableHead className="text-right">{language === "es" ? "Acciones" : "Actions"}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reviews.map((review) => (
              <TableRow key={review.review_id} data-testid={`admin-review-row-${review.review_id}`}>
                <TableCell className="text-sm text-muted-foreground">{formatDate(review.created_at)}</TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">{review.user_username}</p>
                    <p className="text-xs text-muted-foreground">{review.user_email}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="gap-1" data-testid={`admin-review-rating-${review.review_id}`}>
                    <Star className="h-3 w-3 fill-current text-amber-400" />
                    {review.rating}/5
                  </Badge>
                </TableCell>
                <TableCell className="max-w-md">
                  <Button type="button" variant="ghost" className="h-auto w-full justify-start px-0 text-left" onClick={() => setSelectedReview(review)} data-testid={`admin-review-message-button-${review.review_id}`}>
                    <span className="line-clamp-3 whitespace-pre-wrap text-sm">{review.message || "—"}</span>
                  </Button>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{review.video_filename || review.video_id}</TableCell>
                <TableCell className="text-right">
                  <Button type="button" variant="outline" size="sm" onClick={() => setReviewToDelete(review)} data-testid={`admin-review-delete-button-${review.review_id}`}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    {language === "es" ? "Eliminar" : "Delete"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!selectedReview} onOpenChange={() => setSelectedReview(null)}>
        <DialogContent className="max-w-2xl" closeButtonTestId="admin-review-detail-close-button" data-testid="admin-review-detail-dialog">
          <DialogHeader>
            <DialogTitle>{language === "es" ? "Detalle del review" : "Review detail"}</DialogTitle>
            <DialogDescription>{selectedReview ? `${selectedReview.user_username} • ${formatDate(selectedReview.created_at)}` : ""}</DialogDescription>
          </DialogHeader>
          {selectedReview && (
            <div className="space-y-4">
              <div className="grid gap-3 rounded-2xl border border-border bg-muted/20 p-4 text-sm sm:grid-cols-2">
                <div><span className="font-medium">Usuario:</span> {selectedReview.user_username}</div>
                <div><span className="font-medium">Email:</span> {selectedReview.user_email}</div>
                <div><span className="font-medium">Video:</span> {selectedReview.video_filename || selectedReview.video_id}</div>
                <div><span className="font-medium">Rating:</span> {selectedReview.rating}/5</div>
              </div>
              <div className="rounded-2xl border border-border p-4">
                <p className="mb-2 text-sm font-medium">{language === "es" ? "Mensaje completo" : "Full message"}</p>
                <p className="whitespace-pre-wrap text-sm leading-7 text-foreground">{selectedReview.message || "—"}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!reviewToDelete} onOpenChange={(open) => !open && setReviewToDelete(null)}>
        <AlertDialogContent data-testid="admin-review-delete-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{language === "es" ? "Eliminar review" : "Delete review"}</AlertDialogTitle>
            <AlertDialogDescription>{language === "es" ? "Esta acción borrará el review de forma permanente." : "This will permanently delete the review."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-review-delete-cancel-button">{language === "es" ? "Cancelar" : "Cancel"}</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-review-delete-confirm-button" onClick={() => reviewToDelete && onDeleteReview(reviewToDelete.review_id)}>{language === "es" ? "Eliminar" : "Delete"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};